import { Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Typography } from '@/components/ui/typography'
import { FlaggedObjectiveList } from '@/components/custom/ogsm/flagged-objective-list'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { useReopenOgsmSession } from '@/hooks/use-ogsm'
import type { OgsmBoard } from '@/types/api'

/** Asks first. A refusal stays in the dialog with the backend's reason. */
export function ReopenSessionDialog({
  companyId,
  board,
  open,
  onOpenChange,
  onReopened,
}: {
  companyId: string
  board: OgsmBoard
  open: boolean
  onOpenChange: (open: boolean) => void
  onReopened: () => void
}) {
  const { session } = board
  const reopen = useReopenOgsmSession(companyId, session.id, onReopened)
  const flagged = board.objectives.filter((o) => o.includeInScorecard)
  const changeOpen = (next: boolean) => {
    if (!next) reopen.reset()
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent className="max-w-120 px-4 py-5">
        <DialogHeader className="mb-4">
          <DialogTitle>{OGSM_COPY.reopenTitle(session.fiscalYear)}</DialogTitle>
          <DialogDescription>{OGSM_COPY.reopenBody}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3.5">
          <FlaggedObjectiveList
            label={OGSM_COPY.comingOffScorecard}
            objectives={flagged}
          />
          <div className="rounded-md border border-primary/10 bg-primary/5 px-3 py-2.5">
            <Typography variant="body-sm" className="text-primary/60">
              {OGSM_COPY.keepsFlagNote}
            </Typography>
          </div>
          {reopen.error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2.5"
            >
              <Typography variant="body-sm" className="text-destructive">
                {ogsmErrorMessage(reopen.error)}
              </Typography>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => changeOpen(false)}>
            {OGSM_COPY.leavePublished}
          </Button>
          <Button
            disabled={reopen.isPending}
            onClick={() =>
              reopen.mutate(undefined, {
                onSuccess: () => changeOpen(false),
              })
            }
          >
            <Undo2 />
            {reopen.isPending ? OGSM_COPY.reopening : OGSM_COPY.reopenAsDraft}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
